/**
 * Power Availability — per-type preferences sub-form (Workflow Prompt 2).
 *
 * SSOT — `powerRequirementMW` lived here pre-SSOT but duplicated
 * `SiteEntity.projectIntent.powerRequirementMW`. It was deleted from
 * `PowerAvailabilityPreferences` in SSOT-3; this form now collects only
 * report-type-only Power Availability fields:
 *   - timeToPowerCriticalDate (distinct from the in-service date)
 *   - renewablesPreference
 *   - btmStrategy (required)
 *   - curtailmentRiskTolerance
 *   - substation distance thresholds (amber + red)
 *
 * Pack: docs/prompt-packs/Site_SSOT_Prompt.md
 *
 * Required field after SSOT: btmStrategy.
 */
import React from 'react';
import { Info } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Slider } from '../../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import type { PowerAvailabilityPreferences } from '../../../types/siteRag';
import { FieldRow, type ValidationResult } from './shared';
import { validatePowerAvailabilityPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validatePowerAvailabilityPreferences };

export type { ValidationResult };

interface Props {
  value: PowerAvailabilityPreferences | undefined;
  onChange: (next: PowerAvailabilityPreferences) => void;
  /**
   * SSOT — opens the Site Project Intent editor when the user wants to
   * edit the canonical MW / cooling / in-service-date fields. Optional;
   * see FeasibilityPreferences.tsx for parity rationale.
   */
  onOpenSiteIntent?: () => void;
}

export const PowerAvailabilityPreferencesForm: React.FC<Props> = ({ value: rawValue, onChange, onOpenSiteIntent }) => {
  const value: PowerAvailabilityPreferences = (rawValue ?? {}) as PowerAvailabilityPreferences;
  const set = <K extends keyof PowerAvailabilityPreferences>(key: K, v: PowerAvailabilityPreferences[K]) => {
    onChange({ ...value, [key]: v });
  };

  // Slider operates on a single value; defaults reflect industry rules of thumb.
  const ambermi = value.maxSubstationDistanceMiAmber ?? 5;
  const redmi = value.maxSubstationDistanceMiRed ?? 15;

  return (
    <div className="space-y-5">
      {/* SSOT — shared inputs live on Site Project Intent. */}
      <div
        data-testid="ssot-summary-power-availability"
        className="text-[10px] text-text-muted bg-state-hover border border-border px-3 py-2"
      >
        Power requirement (MW) is set at <strong>Site Project Intent</strong>.{onOpenSiteIntent ? (
          <>{' '}
            <button
              type="button"
              onClick={onOpenSiteIntent}
              className="text-primary underline"
              data-testid="ssot-open-site-intent-power-availability"
            >
              Edit Site Project Intent →
            </button>
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FieldRow
          label="Time to first power — critical date"
          fieldKey="timeToPowerCriticalDate"
          help="The date by which energization must be complete. Drives gating in the action plan and queue feasibility."
        >
          <Input
            type="date"
            value={value.timeToPowerCriticalDate ?? ''}
            onChange={(e) => set('timeToPowerCriticalDate', e.target.value || undefined)}
            className="h-9 text-sm"
          />
        </FieldRow>

        <FieldRow
          label="Renewables preference"
          fieldKey="renewablesPreference"
          help="Required = solar/wind must be in the plan. Avoided = exclude renewables from the recommendation."
        >
          <Select value={value.renewablesPreference ?? ''} onValueChange={(v) => set('renewablesPreference', v as PowerAvailabilityPreferences['renewablesPreference'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="required">Required</SelectItem>
              <SelectItem value="preferred">Preferred</SelectItem>
              <SelectItem value="agnostic">Agnostic</SelectItem>
              <SelectItem value="avoided">Avoided</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow
          label="BTM strategy"
          fieldKey="btmStrategy"
          required
          help="Grid-only = pursue grid interconnection only. BTM-only = self-generation, with the connection mode decided separately. Hybrid = grid plus on-site supply. Islanded = no grid connection planned; permits, fuel and reliability still require diligence."
        >
          <Select value={value.btmStrategy ?? ''} onValueChange={(v) => set('btmStrategy', v as PowerAvailabilityPreferences['btmStrategy'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="grid-only">Grid only</SelectItem>
              <SelectItem value="btm-only">BTM only</SelectItem>
              <SelectItem value="islanded">Islanded / off-grid</SelectItem>
              <SelectItem value="hybrid">Hybrid</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow
          label="Curtailment risk tolerance"
          fieldKey="curtailmentRiskTolerance"
          help="How much potential generation curtailment is acceptable. Drives the curtailment-risk section's verdict thresholds."
        >
          <Select value={value.curtailmentRiskTolerance ?? ''} onValueChange={(v) => set('curtailmentRiskTolerance', v as PowerAvailabilityPreferences['curtailmentRiskTolerance'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low — block on any meaningful curtailment</SelectItem>
              <SelectItem value="medium">Medium — accept &lt;5% expected</SelectItem>
              <SelectItem value="high">High — accept &gt;5% if economics work</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
      </div>

      <div className="border-t border-border pt-4 space-y-4">
        <div className="flex items-center gap-1.5">
          <Label className="type-section text-text-secondary">
            Substation distance scoring
          </Label>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" tabIndex={-1} className="text-text-muted hover:text-text-primary">
                  <Info className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] text-xs">
                Sites within the amber range score green; between amber and red score amber; beyond red score red.
                Organization admins can set company-wide defaults; this form supplies the values the report uses.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div data-testid="pref-field-maxSubstationDistanceMiAmber">
          <div className="flex items-center justify-between text-xs">
            <span className="text-text-muted">Amber threshold</span>
            <span className="font-mono text-text-primary">{ambermi.toFixed(1)} mi</span>
          </div>
          <Slider
            value={[ambermi]}
            min={0.5}
            max={50}
            step={0.5}
            onValueChange={(arr) => set('maxSubstationDistanceMiAmber', arr[0])}
            className="mt-1"
          />
        </div>

        <div data-testid="pref-field-maxSubstationDistanceMiRed">
          <div className="flex items-center justify-between text-xs">
            <span className="text-text-muted">Red threshold</span>
            <span className="font-mono text-text-primary">{redmi.toFixed(1)} mi</span>
          </div>
          <Slider
            value={[redmi]}
            min={1}
            max={100}
            step={1}
            onValueChange={(arr) => set('maxSubstationDistanceMiRed', arr[0])}
            className="mt-1"
          />
          {ambermi >= redmi && (
            <p className="text-[10px] text-warning mt-1">Amber threshold should be less than red.</p>
          )}
        </div>
      </div>
    </div>
  );
};
